import { Input as AInput, Checkbox, Col, Form, FormInstance, Row, Spin } from 'antd';
import React, { Dispatch, FC, useMemo, useState } from 'react';

import { debounce } from 'lodash';
import { FormattedMessage } from 'react-intl';
import styled from 'styles/styled-components';
import utilsMessages from 'utils/messages';

import Input from 'components/TrackingComponents/Input';

import translations from 'translations';

import { LoadingOutlined } from '@ant-design/icons';
import { compose } from 'redux';
import injectSaga from 'utils/injectSaga';
import messages from '../messages';

import { connect } from 'react-redux';

import { fetchExternalInvoice } from '../action';
import saga from './redInvoiceSaga';

const FormItem = styled(Form.Item)`
  margin-bottom: 12px !important;
`;

const AntdInput = styled(AInput)<any>``;

interface Props {
  form?: FormInstance<any>;
  setFetchingInvoice?: (state: boolean) => void;
  required?: boolean;
}

interface IDispatchProps {
  fetchRedInvoice: (taxCode: string, cb: (data: any) => void) => void;
}

interface IStateProps {}

type OwnProps = Props & IDispatchProps & IStateProps;

const antIcon = <LoadingOutlined style={{ fontSize: 18 }} spin />;

const RedInvoice: FC<OwnProps> = ({ form, fetchRedInvoice, setFetchingInvoice, required }) => {
  const [loading, setLoading] = useState(false);
  const watchIsSameAddress = Form.useWatch('isSameAddress', form);

  const debouncedRedInvoiceSearch = useMemo(
    () =>
      debounce(async (val: string) => {
        if (val === '') {
          return;
        }
        setLoading(true);
        setFetchingInvoice?.(true);
        fetchRedInvoice(val, (data) => {
          if (data) {
            form?.setFieldsValue({
              redInvoiceCompanyName: data?.name || '',
              redInvoiceAddress: data?.address || '',
            });
          }
          setLoading(false);
          setFetchingInvoice?.(false);
        });
      }, 400),
    [fetchRedInvoice, form],
  );

  return (
    <>
      <Row gutter={12} id="first-row">
        <Col xs={24} lg={12}>
          <FormattedMessage {...messages.taxCode}>
            {(taxCode: string) => (
              <FormattedMessage {...utilsMessages.fieldRequired} values={{ field: taxCode }}>
                {(fieldRequired) => (
                  <FormattedMessage {...messages.taxCodePlaceHolder}>
                    {(taxCodePlaceHolder) => (
                      <FormItem
                        name="taxCode"
                        colon={false}
                        required={false}
                        label={taxCode}
                        hasFeedback
                        rules={[
                          {
                            required: !!required,
                            message: fieldRequired,
                          },
                          {
                            validator: (_, value) => {
                              if (!value) return Promise.resolve();
                              const errorMessage = translations(messages.taxCodeInvalid);
                              if (!/^[0-9-]+$/.test(value)) {
                                return Promise.reject(new Error(errorMessage));
                              }
                              const isValid =
                                /^\d{10}$/.test(value) || /^\d{12}$/.test(value) || /^\d{10}-\d{3}$/.test(value);
                              if (!isValid) {
                                return Promise.reject(new Error(errorMessage));
                              }
                              return Promise.resolve();
                            },
                          },
                        ]}
                      >
                        <AInput
                          placeholder={String(taxCodePlaceHolder)}
                          suffix={<Spin style={{ display: loading ? 'block' : 'none' }} indicator={antIcon} />}
                          disabled={loading}
                          onChange={(event) => {
                            const value = event.target.value;
                            debouncedRedInvoiceSearch(value);
                            return value;
                          }}
                        />
                      </FormItem>
                    )}
                  </FormattedMessage>
                )}
              </FormattedMessage>
            )}
          </FormattedMessage>
        </Col>
        <Col xs={24} lg={12}>
          <FormattedMessage {...messages.companyName}>
            {(companyName: string) => (
              <FormattedMessage {...utilsMessages.fieldRequired} values={{ field: companyName }}>
                {(fieldRequired) => (
                  <FormattedMessage {...messages.companyNamePlaceHolder}>
                    {(companyNamePlaceHolder) => (
                      <FormItem
                        name="redInvoiceCompanyName"
                        label={companyName}
                        colon={false}
                        required={false}
                        hasFeedback
                        rules={[{ required: !!required, message: fieldRequired }]}
                      >
                        <Input
                          InputComponent={AntdInput}
                          trackingCategory="Create Store Form | Input"
                          trackingAction="Enter Phone"
                          type="text"
                          placeHolder={companyNamePlaceHolder}
                          suffix={<Spin style={{ display: loading ? 'block' : 'none' }} indicator={antIcon} />}
                          disabled={loading}
                        />
                      </FormItem>
                    )}
                  </FormattedMessage>
                )}
              </FormattedMessage>
            )}
          </FormattedMessage>
        </Col>
      </Row>
      <Row gutter={12}>
        <Col span={24}>
          <FormattedMessage {...messages.emailReceiveRedInvoice}>
            {(emailReceiveRedInvoice: string) => (
              <FormattedMessage {...utilsMessages.fieldRequired} values={{ field: emailReceiveRedInvoice }}>
                {(fieldRequired) => (
                  <FormattedMessage {...messages.emailReceiveRedInvoicPlaceHolder}>
                    {(emailReceiveRedInvoicPlaceHolder) => (
                      <FormItem
                        name="redInvoiceEmail"
                        colon={false}
                        required={false}
                        label={emailReceiveRedInvoice}
                        rules={[
                          {
                            required: false,
                            message: fieldRequired,
                          },
                          {
                            validator: (_, value) => {
                              if (!value || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
                                return Promise.resolve();
                              }
                              return Promise.reject(new Error(translations(messages.emailInvalid)));
                            },
                          },
                        ]}
                      >
                        <Input
                          InputComponent={AntdInput}
                          trackingCategory="Create Store Form | Input"
                          trackingAction="Enter store name"
                          type="text"
                          placeHolder={emailReceiveRedInvoicPlaceHolder}
                          suffix={<Spin style={{ display: loading ? 'block' : 'none' }} indicator={antIcon} />}
                          disabled={loading}
                        />
                      </FormItem>
                    )}
                  </FormattedMessage>
                )}
              </FormattedMessage>
            )}
          </FormattedMessage>
        </Col>
      </Row>
      <Form.Item
        name="isSameAddress"
        valuePropName="checked"
        colon={false}
        required={false}
        style={{ marginBottom: '2px' }}
      >
        <Checkbox>{translations(messages.redInvoiceAddressCheckbox)}</Checkbox>
      </Form.Item>
      {!watchIsSameAddress && (
        <Row gutter={12}>
          <Col span={24}>
            <FormattedMessage {...messages.redInvoiceAddress}>
              {(redInvoiceAddress: string) => (
                <FormattedMessage {...utilsMessages.fieldRequired} values={{ field: redInvoiceAddress }}>
                  {(fieldRequired) => (
                    <FormattedMessage {...messages.redInvoiceAddressPlaceHolder}>
                      {(redInvoiceAddressPlaceHolder) => (
                        <FormItem
                          name="redInvoiceAddress"
                          colon={false}
                          required={false}
                          label={redInvoiceAddress}
                          rules={[
                            {
                              required: false,
                              message: fieldRequired,
                            },
                          ]}
                        >
                          <Input
                            InputComponent={AntdInput}
                            trackingCategory="Create Store Form | Input"
                            trackingAction="Enter store name"
                            type="text"
                            placeHolder={redInvoiceAddressPlaceHolder}
                            suffix={<Spin style={{ display: loading ? 'block' : 'none' }} indicator={antIcon} />}
                            disabled={loading}
                          />
                        </FormItem>
                      )}
                    </FormattedMessage>
                  )}
                </FormattedMessage>
              )}
            </FormattedMessage>
          </Col>
        </Row>
      )}
    </>
  );
};

const mapDispatchToProps = (dispatch: Dispatch<any>): IDispatchProps => ({
  fetchRedInvoice: (taxCode, cb) => dispatch(fetchExternalInvoice(taxCode, cb)),
});

const withConnect = connect<IStateProps, IDispatchProps>(() => ({}), mapDispatchToProps);

const withSaga = injectSaga<Props>({ key: 'redInvoice', saga: saga });

export default compose(withConnect, withSaga)(RedInvoice);
